import React from 'react';
import useExcelStyles from '../useExcelStyles';
import useNoRowsTemplate from '../useNoRowsOverlay';
import useLoadingOverlay from '../useLoadingOverlay';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '../../../redux/store';
import { setDialogMode, openDialog, selectOrganization } from '../../../redux/slices/organization';
import dateTimeCellCallback from '../../../utils/ag-grid/processCellCallback/dateTimeCellCallback';
import emailCellCallback from '../../../utils/ag-grid/processCellCallback/emailCellCallback';
import organizationBaseIdCellCallback from '../../../utils/ag-grid/processCellCallback/organizationBaseIdCellCallback';
import steelMakerCellCallback from '../../../utils/ag-grid/processCellCallback/steelMakerCellCallback';
import businessTypeCellCallback from '../../../utils/ag-grid/processCellCallback/businessTypeCellCallback';
import steelCategoryCellCallback from '../../../utils/ag-grid/processCellCallback/steelCategoryCellCallback';
import { TOOLTIP_SHOW_DELAY } from '../../../constants';
import type { OrganizationType } from '../../../types/organization';
import type { ProcessCellCallbackParams } from '../../../types/common';
import type { ColDef, ExcelStyle, GetContextMenuItemsParams, GridOptions, MenuItemDef } from 'ag-grid-community';

const useGridOptionsManageOrganization = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commonExcelStyles } = useExcelStyles();

  // Excelのスタイル設定
  // ----------------------------------------
  const excelStyles: ExcelStyle[] = [...commonExcelStyles];

  // Overlayの設定
  // ----------------------------------------
  const overlayLoadingTemplate: string = useLoadingOverlay();
  const overlayNoRowsTemplate: string = useNoRowsTemplate();

  // rowNodeIdの設定
  // ----------------------------------------
  const getRowNodeId = (params: OrganizationType): string => {
    if (params.id !== undefined) {
      return String(params.id);
    } else {
      return params.createdAt;
    }
  };

  // 右クリックメニューの設定
  // ----------------------------------------
  const getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    // console.log(params);
    if (params.node) {
      const data: OrganizationType = params.node.data;
      dispatch(selectOrganization(data));
      // Excel用の変換を行う
      const processCellCallback = (params: ProcessCellCallbackParams): string => {
        // console.log(params.value);
        const myColDef: ColDef = params.column.getColDef();
        const myField: string | undefined = myColDef.field ?? myColDef.colId;
        if (myField === 'createdAt' || myField === 'updatedAt') {
          const formattedValue: string = dateTimeCellCallback(params);
          // console.log(formattedValue);
          return formattedValue;
        } else if (myField === 'createdBy' || myField === 'updatedBy') {
          const formattedValue: string = emailCellCallback(params);
          // console.log(formattedValue);
          return formattedValue;
        } else if (myField === 'organizationBaseId') {
          const organizationBaseName: string = organizationBaseIdCellCallback(params);
          // console.log(organizationBaseName);
          return organizationBaseName;
        } else if (myField === 'steelMaker') {
          const steelMakerName: string = steelMakerCellCallback(params);
          // console.log(steelMakerName);
          return steelMakerName;
        } else if (myField === 'businessType') {
          const businessTypeName: string = businessTypeCellCallback(params);
          // console.log(businessTypeName);
          return businessTypeName;
        } else if (myField === 'steelCategory') {
          const steelCategoryName: string = steelCategoryCellCallback(params);
          // console.log(steelCategoryName);
          return steelCategoryName;
        } else {
          return params.value ?? '';
        }
      };
      // カスタムメニューの作成
      const editMenu = {
        name: t('edit'),
        action: () => {
          dispatch(setDialogMode('UPDATE'));
          dispatch(openDialog());
        },
      };
      const deleteMenu = {
        name: t('delete'),
        action: () => {
          dispatch(setDialogMode('DELETE'));
          dispatch(openDialog());
        },
      };
      const exportMenu = {
        icon: '<span class="ag-icon ag-icon-save" unselectable="on"></span>',
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => params.api.exportDataAsCsv({ processCellCallback }),
          },
          {
            name: 'Excel Export (.xlsx)',
            action: () => params.api.exportDataAsExcel({ processCellCallback }),
          },
        ],
      };

      const menuItems = [editMenu, deleteMenu, 'separator', exportMenu];
      // 豊田通商は編集できない
      const menuItemsTradingCompany = [exportMenu];

      if (!data.isTradingCompany) {
        return menuItems;
      } else {
        return menuItemsTradingCompany;
      }
    } else {
      return [];
    }
  };

  // GridOptions全体
  // ----------------------------------------
  const gridOptions: GridOptions = {
    allowContextMenuWithControlKey: true,
    enableRangeSelection: true,
    excelStyles,
    getContextMenuItems,
    getRowNodeId,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    tooltipShowDelay: TOOLTIP_SHOW_DELAY,
  };

  return { gridOptions };
};

export default useGridOptionsManageOrganization;
